'use client';

import { Card, CardContent } from '@up2stay/up2promo-ui';
import { Icon } from '@iconify/react';
import ButtonWithEnter from './components/button-with-enter';
import { useAppState } from '@up2promo-setup-frontend/core/store/app-state.store';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { ProductsPublicApi } from '@up2stay/up2promo-api-client';
import type { AxiosError } from 'axios';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

type ResetStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ResetPage() {
  const params = useParams<{ resetCode: string }>();
  const resetCode = params.resetCode as string;
  const translations = useDictionary();
  const hideSpinner = useAppState((state) => state.hideSpinner);
  const [status, setStatus] = useState<ResetStatus>(() =>
    resetCode ? 'idle' : 'error',
  );
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      hideSpinner();
    }
  }, [hideSpinner]);

  const handleConfirmReset = async () => {
    setStatus('submitting');
    try {
      await ProductsPublicApi.verifyAndDeleteResetCode({
        path: {
          resetCode,
        },
      });
      setStatus('success');
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to verify reset code:', axiosError);
      setStatus('error');
    }
  };

  const handleBackToWebsite = () => {
    window.location.href = 'https://www.up2promo.com';
  };

  return (
    <div className="flex min-h-dvh w-full flex-col items-center justify-center gap-4 bg-background p-4 pt-16 sm:gap-6 sm:p-6 sm:pt-20 md:pt-24 lg:p-32">
      {status === 'idle' && (
        <div className="flex w-full max-w-2xl flex-col gap-4 sm:gap-6">
          <Icon
            icon="mdi:alert-circle-outline"
            className="text-[5rem] text-foreground sm:text-[6rem]"
          />
          <motion.h1
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl lg:text-4xl"
          >
            {translations.pages.resetPage.confirm.title}
          </motion.h1>
          <motion.p
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-prose text-sm text-foreground/70 md:text-base"
          >
            {translations.pages.resetPage.confirm.description}
          </motion.p>
          <motion.div
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <ButtonWithEnter
              text={translations.pages.resetPage.confirm.button}
              onClick={handleConfirmReset}
            />
          </motion.div>
        </div>
      )}

      {status === 'submitting' && (
        <div className="flex flex-col items-center justify-center gap-4 sm:gap-6">
          <Icon
            icon="svg-spinners:ring-resize"
            className="text-4xl text-foreground sm:text-6xl"
          />
          <motion.p
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-sm text-foreground/70 md:text-base"
          >
            {translations.pages.resetPage.loading}
          </motion.p>
        </div>
      )}

      {status === 'success' && (
        <div className="flex w-full max-w-2xl flex-col gap-4 sm:gap-6">
          <Icon
            icon="ix:cloud-success"
            className="text-[5rem] text-foreground sm:text-[6rem]"
          />
          <motion.h1
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl lg:text-4xl"
          >
            {translations.pages.resetPage.success.title}
          </motion.h1>
          <motion.p
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-prose text-sm text-foreground/70 md:text-base"
          >
            {translations.pages.resetPage.success.description}
          </motion.p>
          <motion.div
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <ButtonWithEnter
              text={translations.pages.resetPage.success.button}
              onClick={handleBackToWebsite}
            />
          </motion.div>
        </div>
      )}

      {status === 'error' && (
        <div className="flex w-full max-w-2xl flex-col gap-4 sm:gap-6">
          <Icon
            icon="mdi:alert-circle-outline"
            className="text-[5rem] text-destructive sm:text-[6rem]"
          />
          <motion.h1
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl lg:text-4xl"
          >
            {translations.pages.resetPage.error.title}
          </motion.h1>
          <motion.p
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-prose text-sm text-foreground/70 md:text-base"
          >
            {translations.pages.resetPage.error.description}
          </motion.p>

          <motion.div
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-3 flex max-w-2xl flex-col gap-2 sm:mt-4 sm:gap-3"
          >
            <p className="text-sm font-medium text-foreground">
              {translations.pages.resetPage.error.contactOptions.title}
            </p>
            <div className="flex flex-col gap-2 sm:gap-3">
              <Card>
                <a href="tel:+4924619160861">
                  <CardContent className="flex items-center gap-3 p-4 transition-colors hover:bg-accent">
                    <Icon
                      icon="material-symbols:phone-in-talk-rounded"
                      className="text-foreground"
                      width={24}
                    />
                    <span className="text-sm text-foreground">
                      {translations.pages.resetPage.error.contactOptions.phone}:
                      +49 2461 9160861
                    </span>
                  </CardContent>
                </a>
              </Card>
              <Card>
                <a href="mailto:contact@up2promo.com">
                  <CardContent className="flex items-center gap-3 p-4 transition-colors hover:bg-accent">
                    <Icon
                      icon="material-symbols:mail"
                      className="text-foreground"
                      width={24}
                    />
                    <span className="text-sm text-foreground">
                      {translations.pages.resetPage.error.contactOptions.email}:
                      contact@up2promo.com
                    </span>
                  </CardContent>
                </a>
              </Card>
              <Card>
                <a
                  href="https://www.up2promo.com/pages/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <CardContent className="flex items-center gap-3 p-4 transition-colors hover:bg-accent">
                    <Icon
                      icon="material-symbols:chat-rounded"
                      className="text-foreground"
                      width={24}
                    />
                    <span className="text-sm text-foreground">
                      {
                        translations.pages.resetPage.error.contactOptions
                          .liveChatAndContactForm
                      }
                    </span>
                  </CardContent>
                </a>
              </Card>
            </div>
          </motion.div>

          <motion.div
            custom={3}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-4"
          >
            <ButtonWithEnter
              text={translations.pages.resetPage.error.button}
              onClick={handleBackToWebsite}
            />
          </motion.div>
        </div>
      )}
    </div>
  );
}
